import { createReducer } from 'redux-create-reducer';
import { promiseAction, fetchApi } from '../utils';
import { getAuthorizationHeaders } from './user';
import isEqual from 'lodash/isEqual';
import shuffle from 'lodash/shuffle';
import { extension } from 'mime-to-extensions';

const SET_ROUTE = 'APP/SET_ROUTE';
const OPEN_CONFIRM = 'APP/OPEN_CONFIRM';
const CLOSE_CONFIRM = 'APP/CLOSE_CONFIRM';

const FETCH_BRACKETS_START = 'APP/FETCH_BRACKETS_START';
const FETCH_BRACKETS_SUCCESS = 'APP/FETCH_BRACKETS_SUCCESS';
const FETCH_BRACKETS_ERROR = 'APP/FETCH_BRACKETS_ERROR';

const UPDATE_BRACKET_START = 'APP/UPDATE_BRACKET_START';
const UPDATE_BRACKET_SUCCESS = 'APP/UPDATE_BRACKET_SUCCESS';
const UPDATE_BRACKET_ERROR = 'APP/UPDATE_BRACKET_ERROR';

const UPDATE_PARTICIPANT_START = 'APP/UPDATE_PARTICIPANT_START';
const UPDATE_PARTICIPANT_SUCCESS = 'APP/UPDATE_PARTICIPANT_SUCCESS';
const UPDATE_PARTICIPANT_ERROR = 'APP/UPDATE_PARTICIPANT_ERROR';

const DELETE_PARTICIPANT_START = 'APP/DELETE_PARTICIPANT_START';
const DELETE_PARTICIPANT_SUCCESS = 'APP/DELETE_PARTICIPANT_SUCCESS';
const DELETE_PARTICIPANT_ERROR = 'APP/DELETE_PARTICIPANT_ERROR';

const UPDATE_NODE_START = 'APP/UPDATE_NODE_START';
const UPDATE_NODE_SUCCESS = 'APP/UPDATE_NODE_SUCCESS';
const UPDATE_NODE_ERROR = 'APP/UPDATE_NODE_ERROR';

const UPDATE_NODE_PARTICIPANT_START = 'APP/UPDATE_NODE_PARTICIPANT_START';
const UPDATE_NODE_PARTICIPANT_SUCCESS = 'APP/UPDATE_NODE_PARTICIPANT_SUCCESS';
const UPDATE_NODE_PARTICIPANT_ERROR = 'APP/UPDATE_NODE_PARTICIPANT_ERROR';

const UPLOAD_PHOTO_START = 'APP/UPLOAD_PHOTO_START';
const UPLOAD_PHOTO_SUCCESS = 'APP/UPLOAD_PHOTO_SUCCESS';
const UPLOAD_PHOTO_ERROR = 'APP/UPLOAD_PHOTO_ERROR';

const DELETE_PHOTO_START = 'APP/DELETE_PHOTO_START';
const DELETE_PHOTO_SUCCESS = 'APP/DELETE_PHOTO_SUCCESS';
const DELETE_PHOTO_ERROR = 'APP/DELETE_PHOTO_ERROR';

const DELETE_BRACKET_START = 'APP/DELETE_BRACKET_START';
const DELETE_BRACKET_SUCCESS = 'APP/DELETE_BRACKET_SUCCESS';
const DELETE_BRACKET_ERROR = 'APP/DELETE_BRACKET_ERROR';

const LOAD_BRACKET_START = 'APP/LOAD_BRACKET_START';
const LOAD_BRACKET_SUCCESS = 'APP/LOAD_BRACKET_SUCCESS';
const LOAD_BRACKET_ERROR = 'APP/LOAD_BRACKET_ERROR';

const initialState = {
  barackets: {
    isFetching: false,
    error: '',
    items: [],
    keyword: '',
  },
  route: {},
  confirm: {
    visible: false,
    cancel: 'app.confirm.cancel',
  },
};

const __updateStateBrackets = (brackets, id, _bracket, prepend) => {
  let bracket = _bracket;
  const index = brackets.findIndex((b) => b.id === Number(id));

  if (typeof bracket === 'function') {
    bracket = bracket(brackets[index] || {});
  }

  if (index === -1) {
    if (prepend) {
      return [bracket, ...brackets];
    }
    return [...brackets, bracket];
  }

  const updatedBracket = { ...brackets[index], ...bracket };

  brackets.splice(index, 1, updatedBracket);
  return brackets.filter(Boolean);
};

export default createReducer(initialState, {
  [SET_ROUTE]: (state, { payload }) => ({
    ...state,
    route: payload,
  }),
  [OPEN_CONFIRM]: (state, { payload }) => ({
    ...state,
    confirm: {
      ...initialState.confirm,
      ...payload,
      visible: true,
    },
  }),
  [CLOSE_CONFIRM]: (state) => ({
    ...state,
    confirm: initialState.confirm,
  }),
  [FETCH_BRACKETS_START]: (state) => ({
    ...state,
    barackets: {
      ...state.barackets,
      isFetching: true,
    },
  }),
  [FETCH_BRACKETS_ERROR]: (state, { error }) => ({
    ...state,
    barackets: {
      ...state.barackets,
      isFetching: false,
      error,
    },
  }),
  [FETCH_BRACKETS_SUCCESS]: (state, { payload, params }) => ({
    ...state,
    barackets: {
      ...state.barackets,
      isFetching: false,
      items: payload.data,
      keyword: params.keyword || '',
    },
  }),
  [UPDATE_BRACKET_SUCCESS]: (state, { payload }) => ({
    ...state,
    barackets: {
      ...state.barackets,
      items: __updateStateBrackets(
        [...state.barackets.items],
        payload.data.id,
        payload.data,
        true
      ),
    },
  }),
  [DELETE_BRACKET_SUCCESS]: (state, { params }) => ({
    ...state,
    barackets: {
      ...state.barackets,
      items: __updateStateBrackets([...state.barackets.items], params.id),
    },
  }),
  [LOAD_BRACKET_SUCCESS]: (state, { payload }) => ({
    ...state,
    barackets: {
      ...state.barackets,
      items: __updateStateBrackets(
        [...state.barackets.items],
        payload.data.id,
        payload.data
      ),
    },
  }),
  [UPDATE_PARTICIPANT_SUCCESS]: (state, { payload, params }) => ({
    ...state,
    barackets: {
      ...state.barackets,
      items: __updateStateBrackets(
        [...state.barackets.items],
        params.treeId,
        (bracket) => ({
          ...bracket,
          participants: __updateStateBrackets(
            bracket.participants,
            payload.data.id,
            payload.data
          ),
        })
      ),
    },
  }),
  [UPDATE_NODE_SUCCESS]: (state, { payload }) => ({
    ...state,
    barackets: {
      ...state.barackets,
      items: __updateStateBrackets(
        [...state.barackets.items],
        payload.data.id,
        payload.data
      ),
    },
  }),
  [UPDATE_NODE_PARTICIPANT_SUCCESS]: (state, { payload, params }) => {
    return {
      ...state,
      barackets: {
        ...state.barackets,
        items: __updateStateBrackets(
          [...state.barackets.items],
          params.treeId,
          (bracket) => {
            return {
              ...bracket,
              nodes: bracket.nodes.map((node) => {
                if (node.id !== parseInt(params.nodeId)) {
                  return node;
                }

                const { score, displayNotes, notes } = payload;

                return {
                  ...node,
                  participants: node.participants.map((participant) =>
                    participant.participant_id ===
                    parseInt(params.participantId)
                      ? { ...participant, score, displayNotes, notes }
                      : participant
                  ),
                };
              }),
            };
          }
        ),
      },
    };
  },
  [UPLOAD_PHOTO_SUCCESS]: (state, { payload, params }) => ({
    ...state,
    barackets: {
      ...state.barackets,
      items: __updateStateBrackets(
        [...state.barackets.items],
        params.treeId,
        (bracket) => ({
          ...bracket,
          participants: __updateStateBrackets(
            bracket.participants,
            payload.data.id,
            payload.data
          ),
        })
      ),
    },
  }),
  [DELETE_PHOTO_SUCCESS]: (state, { params }) => ({
    ...state,
    barackets: {
      ...state.barackets,
      items: __updateStateBrackets(
        [...state.barackets.items],
        params.treeId,
        (bracket) => ({
          ...bracket,
          participants: bracket.participants.map((participant) => ({
            ...participant,
            photo: participant.id === params.id ? null : participant.photo,
          })),
        })
      ),
    },
  }),
  [DELETE_PARTICIPANT_SUCCESS]: (state, { params }) => ({
    ...state,
    barackets: {
      ...state.barackets,
      items: __updateStateBrackets(
        [...state.barackets.items],
        params.treeId,
        (bracket) => ({
          ...bracket,
          participants: __updateStateBrackets(
            bracket.participants,
            params.id,
            null
          ),
        })
      ),
    },
  }),
});

const createBracket = (bracket = {}) => {
  const participants = (bracket.participants || []).reduce(
    (result, participant) => ({
      ...result,
      [participant.id]: participant,
    }),
    {}
  );

  return {
    ...bracket,
    participants: Object.values(participants),
    nodes: (bracket.nodes || []).map((node) => {
      return {
        ...node,
        id: node.id,
        winner: node.winnerId,
        participants: node.participantId.map((id) => ({
          ...node.participants.find(
            (participant) => participant.participant_id === id
          ),
          ...participants[id],
          winner: id === node.winnerId,
        })),
      };
    }),
  };
};
export const getBrackets = (state) => state.app.barackets || {};
export const getPathname = (state) => state.app.route.pathname;
export const getActiveTab = (state) => state.app.route.hash;
export const getConfirmData = (state) => state.app.confirm;
export const hasVisibleConfirm = (state) => state.app.confirm.visible;
export const getSearchKeyword = (state) => state.app.brackets.keyword;
export const getBracketById = (state, id) =>
  createBracket(
    getBrackets(state).items.find((bracket) => bracket.id === Number(id))
  );

export const getNodeByIdFromBracket = (nodes, nodeId) =>
  nodes.find(({ id }) => Number(nodeId) === id) || {};

export const getParticipantById = (state, treeId, participantId) => {
  const { participants } = getBracketById(state, treeId) || {};
  if (!participants) {
    return {};
  }

  return participants.find(({ id }) => id === Number(participantId));
};

let bracketFetchPromise;

export const fetchBrackets = (keyword) => {
  const params = ['limit=50'];
  if (keyword) {
    params.push(`title=*${encodeURIComponent(keyword)}*`);
  }
  bracketFetchPromise = promiseAction({
    types: [FETCH_BRACKETS_START, FETCH_BRACKETS_SUCCESS, FETCH_BRACKETS_ERROR],
    promise: (getState) =>
      fetchApi({
        url: `api/v1/tree/search?${params.join('&')}`,
        ...getAuthorizationHeaders(getState()),
      }),
    params: {
      keyword,
    },
  });

  return bracketFetchPromise;
};

const createBracketBody = ({
  title,
  location,
  participantCount,
  displayScores,
  ...customAttributes
}) => ({
  title,
  location,
  participantCount,
  customAttributes,
  displayScores,
});

export const updateBracket = (body, id) => {
  let url = 'api/v1/tree';
  let method = 'POST';

  if (id) {
    url += `/${id}`;
    method = 'PUT';
  }

  return promiseAction({
    types: [UPDATE_BRACKET_START, UPDATE_BRACKET_SUCCESS, UPDATE_BRACKET_ERROR],
    promise: (getState) =>
      fetchApi({
        url,
        method,
        body: JSON.stringify(createBracketBody(body)),
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeaders(getState()).headers,
        },
      }),
  });
};

export const updateParticipant = (body, { treeId, id }) => {
  let url = `api/v2/tree/${treeId}/participant`;
  let method = 'POST';

  if (id) {
    url += `/${id}`;
    method = 'PUT';
  }

  return promiseAction({
    types: [
      UPDATE_PARTICIPANT_START,
      UPDATE_PARTICIPANT_SUCCESS,
      UPDATE_PARTICIPANT_ERROR,
    ],
    params: {
      treeId,
    },
    promise: (getState) =>
      fetchApi({
        url,
        method,
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeaders(getState()).headers,
        },
      }),
  });
};

export const deleteParticipant = ({ treeId, id }) =>
  promiseAction({
    types: [
      DELETE_PARTICIPANT_START,
      DELETE_PARTICIPANT_SUCCESS,
      DELETE_PARTICIPANT_ERROR,
    ],
    params: {
      treeId,
      id,
    },
    promise: (getState) =>
      fetchApi({
        url: `api/v1/tree/${treeId}/participant/${id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeaders(getState()).headers,
        },
      }),
  });

export const updateNodeParticipant = (
  { score, displayNotes, notes },
  { treeId, nodeId, participantId }
) =>
  promiseAction({
    types: [
      UPDATE_NODE_PARTICIPANT_START,
      UPDATE_NODE_PARTICIPANT_SUCCESS,
      UPDATE_NODE_PARTICIPANT_ERROR,
    ],
    params: {
      treeId,
      nodeId,
      participantId,
    },
    customPayload: { score, displayNotes, notes },
    promise: (getState) => {
      return fetchApi({
        url: `api/v2/tree/${treeId}/node/${nodeId}/participant/${participantId}`,
        method: 'PUT',
        body: JSON.stringify({ score, displayNotes, notes }),
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeaders(getState()).headers,
        },
      });
    },
  });

const __nodeUpdateRequest =
  ({ body, treeId, id }) =>
  async (getState) => {
    const fetchData = (isV1) =>
      fetchApi({
        url: `api/${isV1 ? 'v1' : 'v2'}/tree/${treeId}/node/${id}`,
        method: 'PUT',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeaders(getState()).headers,
        },
      });

    const legacyResult = await fetchData(true);
    await fetchData();

    return legacyResult;
  };

const nodeUpdateRequest = ({ body, treeId, id }) =>
  promiseAction({
    types: [UPDATE_NODE_START, UPDATE_NODE_SUCCESS, UPDATE_NODE_ERROR],
    params: {
      treeId,
    },
    promise: __nodeUpdateRequest({ body, treeId, id }),
  });

export const updateNextNode =
  ({ treeId, id }) =>
  (dispatch, getState) => {
    const bracket = getBracketById(getState(), treeId);
    const { next } = getNodeByIdFromBracket(bracket.nodes, id);
    const nextNode = getNodeByIdFromBracket(bracket.nodes, next);

    if (!next) {
      return;
    }

    const participantId = bracket.nodes
      .filter((node) => node.next === next)
      .map(({ winner }) => winner)
      .filter(Boolean);

    if (
      nextNode &&
      !isEqual(
        participantId,
        nextNode.participants.map((participant) => participant.id)
      )
    ) {
      return dispatch(
        updateNode({ participantId, winnerId: '' }, { treeId, id: next })
      );
    }
  };

export const updateNode =
  (body, { treeId, id }) =>
  async (dispatch) => {
    const result = await dispatch(nodeUpdateRequest({ body, treeId, id }));
    dispatch(updateNextNode({ treeId, id }));

    return result;
  };

export const fetchSingleBracket = (id) =>
  promiseAction({
    types: [LOAD_BRACKET_START, LOAD_BRACKET_SUCCESS, LOAD_BRACKET_ERROR],
    promise: async () => {
      await bracketFetchPromise;
      return fetchApi({ url: `api/v1/tree/${id}` });
    },
  });

const getFileFromBase64 = (uri) => {
  const mimeString = uri.split(',')[0].split(':')[1].split(';')[0];
  return fetch(uri)
    .then((res) => res.blob())
    .then((blob) => ({
      file: new Blob([blob], { type: mimeString }),
      filename: `photo.${extension(mimeString)}`,
    }));
};

export const uploadPhoto =
  (image, { treeId, id }) =>
  async (dispatch) => {
    const form = new FormData();
    const { file, filename } = await getFileFromBase64(image);
    form.append('photo', file, filename);

    return dispatch(
      promiseAction({
        types: [UPLOAD_PHOTO_START, UPLOAD_PHOTO_SUCCESS, UPLOAD_PHOTO_ERROR],
        params: { treeId },
        promise: (getState) =>
          fetchApi({
            url: `api/v1/tree/${treeId}/participant/${id}/photo`,
            method: 'POST',
            body: form,
            headers: {
              ...getAuthorizationHeaders(getState()).headers,
            },
          }),
      })
    );
  };

export const deletePhoto = ({ treeId, id }) =>
  promiseAction({
    types: [DELETE_PHOTO_START, DELETE_PHOTO_SUCCESS, DELETE_PHOTO_ERROR],
    params: {
      treeId,
      id,
    },
    promise: (getState) =>
      fetchApi({
        url: `api/v1/tree/${treeId}/participant/${id}/photo`,
        method: 'DELETE',
        headers: {
          ...getAuthorizationHeaders(getState()).headers,
        },
      }),
  });

export const deleteBracket = (id) =>
  promiseAction({
    types: [DELETE_BRACKET_START, DELETE_BRACKET_SUCCESS, DELETE_BRACKET_ERROR],
    params: {
      id,
    },
    promise: (getState) =>
      fetchApi({
        url: `api/v1/tree/${id}`,
        method: 'DELETE',
        headers: {
          ...getAuthorizationHeaders(getState()).headers,
        },
      }),
  });

export const randomizeParticipantsAction =
  (bracketId) => (dispatch, getState) => {
    const bracket = getBracketById(getState(), bracketId);
    const participants = shuffle(bracket.participants);

    return dispatch(
      promiseAction({
        types: [UPDATE_NODE_START, UPDATE_NODE_SUCCESS, UPDATE_NODE_ERROR],
        params: {
          treeId: bracketId,
        },
        promise: async () => {
          await Promise.all(
            bracket.nodes
              .filter(({ initial }) => initial)
              .map(({ id, next }, index) => {
                const currentIndex = index * 2;
                return __nodeUpdateRequest({
                  body: {
                    winnerId: '',
                    id,
                    next,
                    initial: true,
                    participantId: [
                      participants[currentIndex].id,
                      participants[currentIndex + 1].id,
                    ],
                  },
                  treeId: bracketId,
                  id,
                })(getState);
              })
          );

          return fetchApi({ url: `api/v1/tree/${bracketId}` });
        },
      })
    );
  };

export const setRoute = (payload) => ({
  type: SET_ROUTE,
  payload,
});

export const closeConfirm = () => ({
  type: CLOSE_CONFIRM,
});

export const openConfirm = (payload) => ({
  type: OPEN_CONFIRM,
  payload,
});

export const openPopup = (payload) => ({
  type: OPEN_CONFIRM,
  payload,
});
